import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

export const useSwipeNavigation = (totalItems: number) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(window.innerWidth);

  // Measure actual carousel container width
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        containerWidthRef.current = containerRef.current.offsetWidth;
      }
    };
    
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (isTransitioning) return;
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  }, [isTransitioning]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (isTransitioning) return;
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  }, [isTransitioning]);

  const onTouchEnd = useCallback(() => {
    if (touchStart === null || touchEnd === null || touchStartY === null || touchEndY === null || isTransitioning) return;
    
    const deltaX = Math.abs(touchStart - touchEnd);
    const deltaY = Math.abs(touchStartY - touchEndY);
    
    // Only handle horizontal swipes (when horizontal movement is greater than vertical)
    if (deltaX > deltaY && deltaX > 50) {
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > 0;
      const isRightSwipe = distance < 0;

      setIsTransitioning(true);
      
      if (isLeftSwipe) {
        setActiveIndex(prev => Math.min(prev + 1, totalItems - 1));
      } else if (isRightSwipe) {
        setActiveIndex(prev => Math.max(prev - 1, 0));
      }

      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 500);
    }

    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartY(null);
    setTouchEndY(null);
  }, [touchStart, touchEnd, touchStartY, touchEndY, isTransitioning, totalItems]);

  const goToIndex = useCallback((index: number) => {
    if (isTransitioning || index === activeIndex || index < 0 || index >= totalItems) return;
    setIsTransitioning(true);
    setActiveIndex(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [activeIndex, isTransitioning, totalItems]);

  const dragOffsetPercent = useMemo(() => 
    touchStart !== null && touchEnd !== null 
      ? ((touchEnd - touchStart) / containerWidthRef.current) * 100 
      : 0,
    [touchStart, touchEnd]
  );
  
  const isDragging = useMemo(() => 
    touchStart !== null && touchEnd !== null,
    [touchStart, touchEnd]
  );

  return {
    activeIndex,
    setActiveIndex: goToIndex,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isTransitioning,
    dragOffsetPercent,
    isDragging,
    containerRef
  };
};